import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandlerFn,
    HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from 'app/core/auth/auth.service';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { AUTH_CONSTANTS } from 'app/core/auth/auth.types';
import { environment } from 'environments/environment';
import { Observable, catchError, switchMap, throwError } from 'rxjs';

/**
 * Auth interceptor (Angular 19 functional HttpInterceptorFn)
 *
 * Enhanced with:
 * - 401 handling via mutex token refresh (no location.reload())
 * - Auth endpoint skip logic to avoid refresh loops
 * - Direct mode: sends httpOnly cookies via withCredentials for cross-origin API calls
 */
export const authInterceptor = (
    req: HttpRequest<unknown>,
    next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
    const authService = inject(AuthService);

    // Clone the request object
    let newReq = req.clone();

    // Direct mode (non-proxy): prefix relative API URLs with apiUrl and add withCredentials
    if (!environment.APP_PROXY && _isRelativeApiRequest(req.url)) {
        newReq = newReq.clone({
            url: `${environment.apiUrl}/${req.url}`,
            withCredentials: true,
        });
    } else if (!environment.APP_PROXY && _isApiRequest(req.url)) {
        newReq = newReq.clone({ withCredentials: true });
    }

    // If the access token didn't expire, add the Authorization header.
    if (
        authService.accessToken &&
        !AuthUtils.isTokenExpired(authService.accessToken)
    ) {
        newReq = newReq.clone({
            headers: newReq.headers.set(
                'Authorization',
                'Bearer ' + authService.accessToken
            ),
        });
    }

    // Response
    return next(newReq).pipe(
        catchError((error) => {
            if (error instanceof HttpErrorResponse && error.status === 401) {
                return _handle401Error(req, next, error, authService);
            }

            return throwError(() => error);
        })
    );
};

/**
 * Handle 401 errors by attempting token refresh before signing out.
 * Skip refresh for auth endpoints to avoid infinite loops.
 */
function _handle401Error(
    req: HttpRequest<unknown>,
    next: HttpHandlerFn,
    error: HttpErrorResponse,
    authService: AuthService
): Observable<HttpEvent<unknown>> {
    // Don't attempt refresh for auth endpoints (login, refresh, logout) to avoid loops
    if (_isAuthEndpoint(req.url)) {
        return throwError(() => error);
    }

    // Attempt token refresh via mutex, then retry the original request
    return authService.refreshTokenWithMutex().pipe(
        switchMap(() => {
            // Retry original request with new token + credentials
            const retryUrl = (!environment.APP_PROXY && _isRelativeApiRequest(req.url))
                ? `${environment.apiUrl}/${req.url}` : req.url;
            let retryReq = req.clone({
                url: retryUrl,
                headers: req.headers.set(
                    'Authorization',
                    'Bearer ' + authService.accessToken
                ),
            });
            if (!environment.APP_PROXY && _isApiRequest(retryReq.url)) {
                retryReq = retryReq.clone({ withCredentials: true });
            }
            return next(retryReq);
        }),
        catchError(() => {
            // Refresh failed - sign out gracefully (NO location.reload())
            authService.signOut();
            return throwError(() => error);
        })
    );
}

/**
 * Check if URL is an auth endpoint (should not trigger refresh to avoid loops)
 */
function _isAuthEndpoint(url: string): boolean {
    return AUTH_CONSTANTS.AUTH_ENDPOINTS.some(path => url.includes(path));
}

/**
 * Check if URL is a relative API request (e.g. 'api/auth/me' without a protocol).
 * These need to be prefixed with environment.apiUrl in non-proxy mode,
 * otherwise they resolve against the hosting domain (e.g. Firebase Hosting)
 * and return HTML instead of JSON.
 */
function _isRelativeApiRequest(url: string): boolean {
    return url.startsWith('api/') && !url.startsWith('http');
}

/**
 * Check if URL targets our API backend (not static assets, transloco files, etc.)
 */
function _isApiRequest(url: string): boolean {
    return url.includes('/api/');
}
